import {FileUpload} from '@/components/atoms';
import PropTypes from 'prop-types';
import {Controller} from 'react-hook-form';
import {useIntl} from 'react-intl';
import {
  uploadTierImage,
  readFileAsDataUrl,
  convertBase64ToDataUrl,
  cacheImageDataUrl,
  getCachedImageDataUrl
} from '@/services/tierImageService';
import {useState, useEffect, useCallback} from 'react';
import {useErrorContext} from '@/context/Error/useErrorContext';
import {logger} from '@/helpers/logger';

const ACCEPTED_IMAGE_EXTENSIONS = ['image/jpeg', 'image/png'];

const extractFileFromInput = (files) => {
  if (files instanceof File) {
    return files;
  }
  if (files?.length > 0) {
    return files[0];
  }
  return null;
};

export const ImageUploader = ({
  control,
  name,
  label,
  helperText,
  initialImageData
}) => {
  const {formatMessage} = useIntl();
  const {showError} = useErrorContext();
  const [imageUri, setImageUri] = useState('');

  useEffect(() => {
    if (initialImageData) {
      setImageUri(convertBase64ToDataUrl(initialImageData));
    }
  }, [initialImageData]);

  const handleImageUpload = useCallback(
    async (files) => {
      const file = extractFileFromInput(files);

      if (!file) {
        return null;
      }

      if (!ACCEPTED_IMAGE_EXTENSIONS.includes(file.type)) {
        const errorMessage = `Unsupported image format. Supported formats: JPEG, PNG`;
        showError(errorMessage);
        throw new Error(errorMessage);
      }

      try {
        const response = await uploadTierImage(file);
        const imageId = Number(response?.imageUploadedRef);
        const optimizedImage = response?.optimizedImage;

        const dataUrl = await readFileAsDataUrl(optimizedImage);
        setImageUri(dataUrl);

        cacheImageDataUrl(imageId, dataUrl, optimizedImage?.name);

        return imageId;
      } catch (error) {
        setImageUri('');
        showError(formatMessage({id: 'CLUB_PLUS.IMAGE_UPLOAD_FAILED'}));
        throw error;
      }
    },
    [showError, formatMessage]
  );

  const handleImageDelete = useCallback(() => {
    setImageUri('');
  }, []);

  return (
    <div className='h-fit rounded-lg border border-gray-500 p-6'>
      <Controller
        name={name}
        control={control}
        render={({field}) => {
          const displayUri = field.value
            ? getCachedImageDataUrl(field.value)
            : imageUri;

          const onUpload = async (files) => {
            try {
              const imageId = await handleImageUpload(files);
              field.onChange(imageId);
            } catch (error) {
              logger.error('Error in onUpload callback:', error);
            }
          };

          const onUploadDelete = () => {
            handleImageDelete();
            field.onChange('');
          };

          return (
            <FileUpload
              className=''
              description={formatMessage({
                id: 'CLUB_PLUS.TIER_CREATE_IMAGE_UPLOADER'
              })}
              feedbackType='info'
              label={label}
              type='image'
              variant='default'
              imageUri={displayUri}
              onUpload={onUpload}
              onUploadDelete={onUploadDelete}
              helperText={helperText}
            />
          );
        }}
      />
    </div>
  );
};

ImageUploader.propTypes = {
  control: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  helperText: PropTypes.string,
  initialImageData: PropTypes.string
};
